import { Link } from "react-router-dom";
import { mainSiteUrl } from "@/mocks/categories";
import { brandArticles } from "@/mocks/brandArticles";
import JsonLd from "@/components/base/JsonLd";
import ArticleNavbar from "./components/ArticleNavbar";
import ArticleFooter from "./components/ArticleFooter";

const siteUrl = import.meta.env.VITE_SITE_URL || "https://kakobuyfinds.wiki";

const articleSchema = {
  "@context": "https://schema.org",
  "@type": "Article",
  headline: "Kakobuy Sheet — Kompletny Przewodnik po Arkuszu Zakupowym 2026",
  description: "Wszystko, co musisz wiedzieć o Kakobuy Spreadsheet — jak działa, dlaczego warto z niego korzystać i jak znaleźć najlepsze produkty w jednym miejscu.",
  author: {
    "@type": "Organization",
    name: "Kakobuy Finds Wiki",
    url: siteUrl,
  },
  publisher: {
    "@type": "Organization",
    name: "Kakobuy Finds Wiki",
    url: siteUrl,
  },
  datePublished: "2026-07-01",
  dateModified: "2026-07-07",
  mainEntityOfPage: {
    "@type": "WebPage",
    "@id": `${siteUrl}/kakobuy-sheet-przewodnik`,
  },
  inLanguage: "pl-PL",
};

const breadcrumbSchema = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [
    {
      "@type": "ListItem",
      position: 1,
      name: "Strona Główna",
      item: siteUrl,
    },
    {
      "@type": "ListItem",
      position: 2,
      name: "Kakobuy Sheet — Przewodnik",
      item: `${siteUrl}/kakobuy-sheet-przewodnik`,
    },
  ],
};

export default function ArticlePage() {
  const relatedArticles = brandArticles.slice(0, 4);

  return (
    <main className="min-h-screen bg-background-50">
      <JsonLd data={articleSchema} />
      <JsonLd data={breadcrumbSchema} />
      <ArticleNavbar />

      {/* Article Hero */}
      <section className="relative w-full min-h-[500px] md:min-h-[560px] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=Editorial%20photography%20of%20carefully%20organized%20fashion%20pieces%20on%20warm%20ochre%20toned%20desk%20surface%20near%20window%2C%20natural%20morning%20light%20streaming%20through%20sheer%20curtains%2C%20laptop%20displaying%20spreadsheet%20data%20alongside%20fabric%20swatches%20and%20clothing%20items%2C%20modern%20creative%20workspace%20aesthetic%2C%20soft%20focus%20on%20background%20bookshelf%20with%20design%20books%2C%20warm%20and%20productive%20atmosphere%2C%20high%20detail%20on%20textile%20textures%2C%20lifestyle%20business%20hybrid%20mood&width=1600&height=900&seq=article-hero-v2&orientation=landscape"
            alt="Kakobuy Sheet - kompletny przewodnik"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/35 via-black/20 to-black/50"></div>
        </div>

        <div className="relative z-10 w-full max-w-7xl mx-auto px-4 md:px-6">
          <div className="flex flex-col lg:flex-row items-center gap-8 lg:gap-12">
            <div className="flex-1 text-center lg:text-left">
              <span className="inline-block px-4 py-1.5 rounded-full border border-white/30 text-white/90 text-xs font-medium mb-5 whitespace-nowrap">
                Przewodnik
              </span>
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-heading font-extrabold text-white leading-[1.15] mb-4">
                Kakobuy Sheet — Kompletny Przewodnik po Arkuszu Zakupowym 2026
              </h1>
              <p className="text-white/75 text-sm md:text-base max-w-lg mx-auto lg:mx-0 leading-relaxed">
                Wszystko, co musisz wiedzieć o <strong>Kakobuy Spreadsheet</strong> — jak działa,
                dlaczego warto z niego korzystać i jak znaleźć najlepsze produkty w jednym miejscu.
              </p>
              <div className="flex items-center gap-4 mt-6 justify-center lg:justify-start text-white/60 text-xs">
                <span>8 min czytania</span>
                <span className="w-1 h-1 rounded-full bg-white/40"></span>
                <span>Aktualizacja: lipiec 2026</span>
              </div>
            </div>
            <div className="hidden lg:flex flex-1 gap-4 justify-center">
              <div className="w-[180px] h-[240px] rounded-2xl overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=Product%20photography%20of%20a%20sleek%20minimalist%20backpack%20and%20sneakers%20arranged%20on%20warm%20honey%20colored%20wooden%20floor%2C%20soft%20natural%20side%20lighting%20from%20french%20doors%2C%20modern%20urban%20apartment%20interior%20with%20white%20walls%20and%20green%20monstera%20plant%2C%20clean%20Scandinavian%20influenced%20aesthetic%2C%20high%20detail%20on%20material%20textures%20canvas%20and%20suede%2C%20relaxed%20weekend%20lifestyle%20editorial%2C%20aspirational%20everyday%20carry%20mood&width=400&height=550&seq=art-hero-01-v2&orientation=portrait"
                  alt="Kakobuy Spreadsheet produkty"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="w-[200px] h-[160px] rounded-2xl overflow-hidden mt-10">
                <img
                  src="https://readdy.ai/api/search-image?query=Still%20life%20photography%20of%20fashion%20styling%20essentials%20including%20sunglasses%20watch%20and%20scarf%20artfully%20arranged%20on%20warm%20toned%20marble%20console%20table%2C%20golden%20afternoon%20light%20creating%20long%20elegant%20shadows%2C%20sophisticated%20boutique%20hotel%20lobby%20aesthetic%2C%20rich%20amber%20and%20cream%20color%20palette%2C%20luxury%20accessories%20editorial%2C%20high%20detail%20on%20reflective%20surfaces%20and%20fabric%20folds%2C%20refined%20travel%20lifestyle%20mood&width=450&height=360&seq=art-hero-02-v2&orientation=landscape"
                  alt="Kakobuy Spreadsheet akcesoria"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Article Content */}
      <article className="py-14 md:py-20 px-4 md:px-6 bg-background-50">
        <div className="max-w-4xl mx-auto">
          <div className="flex flex-col lg:flex-row gap-12 lg:gap-16">
            <div className="flex-1 prose max-w-none text-foreground-800 leading-relaxed space-y-8">
              {/* Intro */}
              <section>
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 !mb-4">
                  Czym Jest Kakobuy Sheet i Dlaczego Warto Go Znać?
                </h2>
                <p className="text-foreground-700 leading-relaxed">
                  <strong>Kakobuy Sheet</strong> to zaawansowany <strong>arkusz zakupowy</strong>, który
                  zrewolucjonizował sposób, w jaki polscy kupujący znajdują produkty online. Jest to
                  starannie posegregowana baza danych zawierająca tysiące sprawdzonych linków do
                  produktów z różnych kategorii — od <strong>obuwia</strong>, przez <strong>odzież</strong>,
                  aż po <strong>akcesoria</strong> i <strong>perfumy</strong>. W przeciwieństwie do
                  tradycyjnych metod wyszukiwania, <strong>Kakobuy Spreadsheet</strong> oferuje wszystko
                  w jednym miejscu, oszczędzając czas i pieniądze użytkowników.
                </p>
                <p className="text-foreground-700 leading-relaxed">
                  Jeśli dopiero zaczynasz swoją przygodę z <strong>Kakobuy</strong>, ten przewodnik jest
                  dla Ciebie. Jeśli jesteś już doświadczonym użytkownikiem — znajdziesz tutaj zaawansowane
                  wskazówki, które pomogą Ci jeszcze lepiej wykorzystać potencjał tego narzędzia.
                  Wróć na <Link to="/" className="text-primary-600 font-semibold hover:text-primary-700 transition-colors cursor-pointer">stronę główną Kakobuy Finds Wiki</Link>, aby
                  przeglądać wszystkie dostępne kategorie.
                </p>
              </section>

              {/* Section 2 */}
              <section>
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 !mb-4">
                  Jak Działa Kakobuy Spreadsheet?
                </h2>
                <p className="text-foreground-700 leading-relaxed">
                  Mechanizm działania <strong>Kakobuy Spreadsheet</strong> jest niezwykle prosty, a
                  jednocześnie potężny. Cały <strong>arkusz</strong> opiera się na strukturze
                  kategorii i podkategorii, które grupują produkty według typu, marki i ceny.
                  Użytkownik wybiera interesującą go kategorię — na przykład <strong>Shoes</strong> lub
                  <strong> Hoodies</strong> — i natychmiast otrzymuje dostęp do listy starannie
                  wyselekcjonowanych produktów wraz z bezpośrednimi linkami.
                </p>
                <p className="text-foreground-700 leading-relaxed">
                  To, co wyróżnia <strong>Kakobuy Sheet</strong> na tle innych podobnych narzędzi, to
                  regularność aktualizacji. <strong>Spreadsheet</strong> jest odświeżany codziennie,
                  co oznacza, że zawsze masz dostęp do najnowszych ofert. Ponadto, każdy link
                  w <strong>arkuszu Kakobuy</strong> przechodzi weryfikację — eliminujemy nieaktywne
                  oferty i dodajemy tylko te, które pochodzą od zaufanych sprzedawców.
                </p>
                <p className="text-foreground-700 leading-relaxed">
                  Sprawdź nasze <Link to="/" className="text-primary-600 font-semibold hover:text-primary-700 transition-colors cursor-pointer">kategorie produktów na stronie głównej</Link>, aby
                  zobaczyć pełną listę dostępnych działów i rozpocząć przeglądanie.
                </p>
              </section>

              {/* Section 3 */}
              <section>
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 !mb-4">
                  Najważniejsze Kategorie w Kakobuy Sheet
                </h2>
                <p className="text-foreground-700 leading-relaxed">
                  <strong>Kakobuy Spreadsheet</strong> oferuje szeroki wachlarz kategorii, które
                  zaspokoją potrzeby nawet najbardziej wymagających kupujących. Oto najpopularniejsze
                  z nich:
                </p>
                <ul className="list-disc pl-5 space-y-2 text-foreground-700">
                  <li>
                    <strong>Shoes</strong> — Największy wybór butów od marek takich jak Nike, Adidas,
                    New Balance i wielu innych. W <strong>Kakobuy arkusz</strong> Shoes znajdziesz
                    zarówno sneakersy, jak i eleganckie obuwie.
                  </li>
                  <li>
                    <strong>Hoodies/Sweaters</strong> — Bluzy i swetry w najlepszych cenach.
                    <strong> Spreadsheet</strong> zawiera setki modeli od Essentials, Stussy,
                    Gallery Dept i innych popularnych brandów.
                  </li>
                  <li>
                    <strong>T-Shirts</strong> — Koszulki w różnych stylach i kolorach. Idealne
                    na każdą okazję — od casualowych po bardziej eleganckie.
                  </li>
                  <li>
                    <strong>Jackets</strong> — Kurtki i płaszcze na każdą porę roku. W
                    <strong> arkuszu Kakobuy</strong> znajdziesz modele od Arc&apos;teryx,
                    The North Face, Carhartt i innych.
                  </li>
                  <li>
                    <strong>Accessories</strong> — Akcesoria modowe, które dopełnią każdą
                    stylizację. Od czapek po biżuterię — wszystko w jednym <strong>arkuszu</strong>.
                  </li>
                </ul>
                <p className="text-foreground-700 leading-relaxed">
                  Wszystkie kategorie są dostępne bezpośrednio z poziomu
                  <Link to="/" className="text-primary-600 font-semibold hover:text-primary-700 transition-colors cursor-pointer"> naszej strony głównej</Link>. Wystarczy kliknąć wybraną
                  kategorię, aby przejść do pełnej listy produktów na <strong>Kakobuy</strong>.
                </p>
              </section>

              {/* Section 4 */}
              <section>
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 !mb-4">
                  Dlaczego Kakobuy Sheet Jest Lepszy Niż Tradycyjne Wyszukiwanie?
                </h2>
                <p className="text-foreground-700 leading-relaxed">
                  Tradycyjne wyszukiwanie produktów online często wiąże się z przeglądaniem
                  dziesiątek stron, porównywaniem cen i sprawdzaniem wiarygodności sprzedawców.
                  <strong> Kakobuy Spreadsheet</strong> eliminuje te problemy, oferując:
                </p>
                <ul className="list-disc pl-5 space-y-2 text-foreground-700">
                  <li>
                    <strong>Oszczędność czasu</strong> — Wszystkie produkty w jednym
                    <strong> arkuszu</strong>, bez potrzeby przeskakiwania między stronami.
                  </li>
                  <li>
                    <strong>Zweryfikowane linki</strong> — Każdy produkt w <strong>spreadsheecie</strong>
                    jest sprawdzany pod kątem aktualności i wiarygodności sprzedawcy.
                  </li>
                  <li>
                    <strong>Codzienne aktualizacje</strong> — <strong>Kakobuy Sheet</strong> jest
                    odświeżany każdego dnia, więc nigdy nie przegapisz nowej oferty.
                  </li>
                  <li>
                    <strong>Szeroki wybór kategorii</strong> — Od ubrań po akcesoria —
                    <strong> arkusz</strong> obejmuje wszystko, czego potrzebujesz.
                  </li>
                  <li>
                    <strong>Dostępność 24/7</strong> — <strong>Spreadsheet</strong> jest dostępny
                    online przez całą dobę, siedem dni w tygodniu.
                  </li>
                </ul>
              </section>

              {/* Section 5 */}
              <section>
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 !mb-4">
                  Najlepsze Marki Dostępne w Kakobuy Spreadsheet
                </h2>
                <p className="text-foreground-700 leading-relaxed">
                  Jednym z największych atutów <strong>Kakobuy Spreadsheet</strong> jest różnorodność
                  dostępnych marek. W <strong>arkuszu</strong> znajdziesz produkty od zarówno
                  światowych gigantów, jak i niszowych, pożądanych brandów. Oto niektóre z nich:
                </p>
                <div className="grid grid-cols-2 gap-2 my-4">
                  {["Nike", "Adidas", "Jordan", "Stussy", "Essentials", "Arc'teryx", "Carhartt", "Bape", "Supreme", "Gallery Dept"].map((brand) => (
                    <span key={brand} className="px-3 py-2 bg-background-100 rounded-lg text-sm font-medium text-foreground-700 text-center">
                      {brand}
                    </span>
                  ))}
                </div>
                <p className="text-foreground-700 leading-relaxed">
                  Każda z tych marek jest regularnie aktualizowana w <strong>Kakobuy Sheet</strong>,
                  a nowe produkty są dodawane natychmiast po ich pojawieniu się. Dzięki temu
                  zawsze masz dostęp do najświeższych kolekcji i limitowanych edycji.
                </p>
              </section>

              {/* Section 6 */}
              <section>
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 !mb-4">
                  Porady dla Początkujących Użytkowników Kakobuy
                </h2>
                <p className="text-foreground-700 leading-relaxed">
                  Jeśli dopiero zaczynasz korzystać z <strong>Kakobuy Spreadsheet</strong>, oto kilka
                  praktycznych wskazówek, które pomogą Ci szybko odnaleźć się w <strong>arkuszu</strong>:
                </p>
                <ol className="list-decimal pl-5 space-y-3 text-foreground-700">
                  <li>
                    <strong>Zacznij od przeglądania kategorii</strong> — Wybierz interesującą Cię
                    kategorię z menu na <Link to="/" className="text-primary-600 font-semibold hover:text-primary-700 transition-colors cursor-pointer">stronie głównej</Link> i zapoznaj się z dostępnymi produktami.
                  </li>
                  <li>
                    <strong>Sprawdzaj regularnie aktualizacje</strong> — <strong>Spreadsheet</strong>
                    jest odświeżany codziennie, więc warto zaglądać często, aby nie przegapić
                    nowych ofert.
                  </li>
                  <li>
                    <strong>Korzystaj z filtrów</strong> — W <strong>Kakobuy Sheet</strong> możesz
                    sortować produkty według ceny, popularności i daty dodania.
                  </li>
                  <li>
                    <strong>Zapisuj ulubione produkty</strong> — Jeśli znajdziesz coś interesującego,
                    zapisz link, aby wrócić do niego później.
                  </li>
                </ol>
              </section>

              {/* Section 7 */}
              <section>
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 !mb-4">
                  Bezpieczeństwo Korzystania z Kakobuy Sheet
                </h2>
                <p className="text-foreground-700 leading-relaxed">
                  Bezpieczeństwo zakupów online jest kluczowe. <strong>Kakobuy Spreadsheet</strong>
                  został zaprojektowany z myślą o maksymalnej ochronie użytkowników. Wszystkie linki
                  w <strong>arkuszu</strong> są regularnie weryfikowane, a produkty pochodzą od
                  zaufanych sprzedawców. Dodatkowo, <strong>Kakobuy</strong> oferuje system ochrony
                  kupujących, który zabezpiecza Twoje transakcje.
                </p>
                <p className="text-foreground-700 leading-relaxed">
                  Pamiętaj, aby zawsze sprawdzać opinie o sprzedawcy przed dokonaniem zakupu.
                  W <strong>spreadsheecie Kakobuy</strong> znajdziesz tylko sprawdzone oferty, ale
                  dodatkowa weryfikacja nigdy nie zaszkodzi. Jeśli masz jakiekolwiek wątpliwości,
                  wróć na <Link to="/" className="text-primary-600 font-semibold hover:text-primary-700 transition-colors cursor-pointer">stronę główną Kakobuy Finds Wiki</Link>, gdzie znajdziesz więcej
                  informacji na temat bezpiecznych zakupów.
                </p>
              </section>

              {/* Section 8 */}
              <section>
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 !mb-4">
                  Podsumowanie — Czy Warto Korzystać z Kakobuy Sheet?
                </h2>
                <p className="text-foreground-700 leading-relaxed">
                  Zdecydowanie tak. <strong>Kakobuy Sheet</strong> to jedno z najlepszych narzędzi
                  dostępnych obecnie na rynku dla osób, które chcą szybko i sprawnie znajdować
                  produkty online. Dzięki rozbudowanemu <strong>arkuszowi</strong>, codziennym
                  aktualizacjom i szerokiemu wyborowi kategorii, <strong>Kakobuy Spreadsheet</strong>
                  jest niezastąpionym narzędziem zarówno dla początkujących, jak i zaawansowanych
                  kupujących.
                </p>
                <p className="text-foreground-700 leading-relaxed">
                  Niezależnie od tego, czy szukasz <strong>butów</strong>, <strong>bluz</strong>,
                  <strong> kurtek</strong> czy <strong>akcesoriów</strong> — <strong>Kakobuy
                  Spreadsheet</strong> ma wszystko, czego potrzebujesz. A jeśli chcesz dowiedzieć
                  się więcej, odwiedź naszą
                  <Link to="/" className="text-primary-600 font-semibold hover:text-primary-700 transition-colors cursor-pointer"> stronę główną</Link>, gdzie znajdziesz pełną listę kategorii i najnowsze
                  aktualizacje <strong>arkusza</strong>.
                </p>
                <p className="text-foreground-700 leading-relaxed">
                  Dołącz do tysięcy zadowolonych użytkowników <strong>Kakobuy Sheet</strong> już
                  dziś i odkryj, jak łatwe i przyjemne może być znajdowanie najlepszych produktów
                  online. <strong>Spreadsheet Kakobuy</strong> czeka na Ciebie!
                </p>
              </section>

              {/* Related brand articles */}
              <section>
                <h3 className="text-xl font-heading font-bold text-foreground-950 mb-3">
                  Poznaj Inne Arkusze Zakupowe
                </h3>
                <div className="grid grid-cols-2 gap-2">
                  {relatedArticles.map((ba) => (
                    <Link
                      key={ba.slug}
                      to={`/${ba.slug}`}
                      className="p-3 rounded-lg bg-background-100 hover:bg-primary-50 transition-colors cursor-pointer text-sm font-medium text-foreground-700 hover:text-primary-700"
                    >
                      {ba.brandName} Spreadsheet &rarr;
                    </Link>
                  ))}
                </div>
              </section>

              {/* Back to home CTA */}
              <div className="flex flex-col sm:flex-row items-center gap-4 pt-6 border-t border-background-200">
                <Link
                  to="/"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-background-50 rounded-full text-sm font-semibold hover:bg-primary-600 transition-all whitespace-nowrap cursor-pointer"
                >
                  Powrót do strony głównej
                  <i className="ri-arrow-left-line"></i>
                </Link>
                <a
                  href={mainSiteUrl}
                  target="_blank"
                  rel="noopener nofollow"
                  className="inline-flex items-center gap-2 px-6 py-3 border border-foreground-300 text-foreground-700 rounded-full text-sm font-semibold hover:bg-foreground-100 transition-all whitespace-nowrap cursor-pointer"
                >
                  Kakobuy Sheet
                  <i className="ri-arrow-right-up-line"></i>
                </a>
              </div>
            </div>

            {/* Sidebar */}
            <aside className="hidden lg:block w-[280px] flex-shrink-0">
              <div className="sticky top-24">
                <div className="rounded-2xl overflow-hidden bg-background-100 mb-6">
                  <img
                    src="https://readdy.ai/api/search-image?query=Vertical%20editorial%20fashion%20photography%20of%20a%20curated%20moodboard%20on%20warm%20cream%20wall%2C%20polaroid%20photos%20and%20fabric%20swatches%20pinned%20with%20washi%20tape%2C%20soft%20natural%20window%20light%2C%20creative%20studio%20aesthetic%20with%20dried%20flowers%20and%20fashion%20sketches%2C%20artistic%20and%20feminine%20atmosphere%2C%20gentle%20ochre%20and%20blush%20color%20palette%2C%20inspiring%20creative%20workspace%20mood&width=300&height=420&seq=art-sidebar-01-v2&orientation=portrait"
                    alt="Kakobuy Spreadsheet"
                    className="w-full h-auto object-cover object-top"
                  />
                </div>

                <div className="p-5 rounded-2xl bg-background-100">
                  <h4 className="font-heading font-bold text-foreground-950 text-sm mb-3">
                    Spis treści
                  </h4>
                  <ul className="space-y-2 text-xs text-foreground-600">
                    <li>Czym jest Kakobuy Sheet?</li>
                    <li>Jak działa Spreadsheet?</li>
                    <li>Najważniejsze kategorie</li>
                    <li>Dlaczego warto?</li>
                    <li>Najlepsze marki</li>
                    <li>Porady dla początkujących</li>
                    <li>Bezpieczeństwo</li>
                    <li>Podsumowanie</li>
                  </ul>
                </div>

                <div className="mt-6 p-5 rounded-2xl bg-primary-50">
                  <p className="text-xs font-semibold text-primary-700 mb-2">
                    Gotowy na zakupy?
                  </p>
                  <p className="text-xs text-foreground-600 mb-3">
                    Przejdź do Kakobuy Sheet i znajdź najlepsze produkty już teraz.
                  </p>
                  <a
                    href={mainSiteUrl}
                    target="_blank"
                    rel="noopener nofollow"
                    className="inline-flex items-center gap-1.5 text-xs font-semibold text-primary-600 hover:text-primary-700 transition-colors cursor-pointer"
                  >
                    Kakobuy Sheet
                    <i className="ri-arrow-right-up-line"></i>
                  </a>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </article>

      {/* Article CTA */}
      <section className="relative w-full min-h-[400px] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=Fashion%20editorial%20scene%20of%20a%20person%20browsing%20clothing%20rack%20in%20a%20sunlit%20boutique%20interior%2C%20warm%20golden%20hour%20light%20streaming%20through%20large%20shop%20windows%2C%20racks%20of%20carefully%20curated%20garments%20in%20muted%20earth%20tones%20and%20neutrals%2C%20sophisticated%20retail%20experience%20atmosphere%2C%20aspirational%20shopping%20lifestyle%20photography%2C%20soft%20focus%20on%20background%20details%2C%20elegant%20and%20inviting%20composition&width=1600&height=900&seq=art-cta-v2&orientation=landscape"
            alt="Kakobuy Sheet - zacznij zakupy"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/25 to-black/45"></div>
        </div>
        <div className="relative z-10 text-center px-4 md:px-6 max-w-2xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-heading font-extrabold text-white mb-4">
            Gotowy na zakupy?
          </h2>
          <p className="text-white/80 text-sm md:text-base mb-8 leading-relaxed">
            Dołącz do tysięcy zadowolonych użytkowników i odkryj najlepsze okazje w Kakobuy Spreadsheet.
          </p>
          <a
            href={mainSiteUrl}
            target="_blank"
            rel="noopener nofollow"
            className="inline-flex items-center gap-3 px-10 py-4 bg-foreground-950 text-white rounded-full text-sm font-semibold hover:bg-foreground-800 transition-all whitespace-nowrap cursor-pointer"
          >
            <span className="w-8 h-8 flex items-center justify-center rounded-full bg-primary-500">
              <i className="ri-shopping-bag-line text-white text-sm"></i>
            </span>
            Kakobuy Sheet
            <i className="ri-arrow-right-up-line text-white/70"></i>
          </a>
        </div>
      </section>

      <ArticleFooter />
    </main>
  );
}